// ============================================================================
// GroupCover - the picture on every group card and group page
// ----------------------------------------------------------------------------
// A photo when the group has one, otherwise one of eight themed covers
// (deep felt gradients with a faint card-suit pattern) chosen from the
// group's id so the same group always looks the same. Owners can pick a
// theme or upload a photo from the group page.
// ============================================================================
import React from 'react';
import { Box } from '@chakra-ui/react';

export const GROUP_COVER_THEMES: Array<{ name: string; from: string; to: string; accent: string }> = [
  { name: 'Emerald felt', from: '#0b3d2e', to: '#0f6b4a', accent: '#7ff0c3' },
  { name: 'Midnight blue', from: '#0b1e3d', to: '#1f4b8f', accent: '#8ec5ff' },
  { name: 'Royal purple', from: '#2a0f4d', to: '#6d28d9', accent: '#d8b4fe' },
  { name: 'Crimson', from: '#4a0d1a', to: '#b91c1c', accent: '#fca5a5' },
  { name: 'Gold rush', from: '#4a3305', to: '#b8860b', accent: '#fde68a' },
  { name: 'Teal', from: '#083344', to: '#0e7490', accent: '#67e8f9' },
  { name: 'Slate', from: '#111827', to: '#374151', accent: '#cbd5e1' },
  { name: 'Sunset', from: '#4a1d0c', to: '#c2410c', accent: '#fdba74' },
];

const SUITS = ['♠', '♥', '♦', '♣'];

interface Props {
  coverUrl?: string | null;
  coverTheme?: number | null;
  name: string;
  /** Height in px or any CSS height */
  height?: string | number;
  radius?: string;
  children?: React.ReactNode;
  dim?: boolean;
}

const GroupCover: React.FC<Props> = ({ coverUrl, coverTheme, name, height = 140, radius = '16px', children, dim = true }) => {
  const theme = GROUP_COVER_THEMES[Math.abs(Number(coverTheme) || 0) % GROUP_COVER_THEMES.length];
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'G';
  return (
    <Box position="relative" h={height} borderRadius={radius} overflow="hidden"
      bg={coverUrl ? '#0a1420' : `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`}>
      {coverUrl ? (
        <Box as="img" src={coverUrl} alt="" w="100%" h="100%" objectFit="cover" display="block" />
      ) : (
        <>
          {/* faint suit pattern */}
          <Box position="absolute" inset={0} opacity={0.14} fontSize="34px" lineHeight="44px" color={theme.accent}
            style={{ letterSpacing: '18px', whiteSpace: 'nowrap', overflow: 'hidden', userSelect: 'none' }} aria-hidden>
            {Array.from({ length: 6 }).map((_, row) => (
              <Box key={row} pl={row % 2 ? '26px' : 0}>{SUITS.join(' ').repeat(8)}</Box>
            ))}
          </Box>
          <Box position="absolute" right="14px" bottom="10px" fontSize="44px" fontWeight="900" color={theme.accent} opacity={0.35} letterSpacing="-2px" aria-hidden>
            {initials}
          </Box>
        </>
      )}
      {dim && <Box position="absolute" inset={0} bg="linear-gradient(180deg, rgba(5,10,20,0) 35%, rgba(5,10,20,0.85) 100%)" />}
      {children && <Box position="absolute" inset={0}>{children}</Box>}
    </Box>
  );
};

export default GroupCover;
